import {
	type EbnView,
	type EbnWriteResult,
	type EntryAnalysis,
	type EntryAnalysisQueryInput,
	type EntryAnalysisRefresh,
	type EntryAnalytics,
	type EntryByEvent,
	type EntryMatrix,
	type EntryOrgSort,
	type EntryOrganizationsResponse,
	type EntryPhase,
	type EntryPhaseBody,
	type EntryReopenBody,
	type EntrySubmitResult,
	type EntryWindow,
	type EntryWindowBody,
	type EntryWindowsQueryInput,
	type EoiRemoveImpact,
	type EoiView,
	type EoiWriteResult,
	type Paginated,
	type PatchEntryWindowBody,
	type PutEbnBody,
	type PutEoiBody,
	isRefreshable,
} from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { getAccessToken } from "../auth/token-store";
import { saveBlob } from "./accreditations";
import { ApiError, api } from "./client";
import { queryClient } from "./query-client";

/**
 * Sport entries (M7b, migration 0038) on the contract's wire shapes
 * (`@seagames/contracts` entries.ts). Every read is ONE request the API
 * answers with one statement; the EOI write is the whole sport list, the
 * EBN write one sport's events. Keys live under `["entries", …]`, so one
 * invalidate refreshes the overview, both editors and the matrix.
 */

const BASE_URL: string = import.meta.env.VITE_API_URL ?? "http://localhost:3001/api";

/** `games_id` + the organisation; a NOC whose scope names one may leave it out. */
export interface EntryScope {
	games_id: string;
	organization_id?: string;
}

const scopeQuery = (s: EntryScope) => (s.organization_id ? { games_id: s.games_id, organization_id: s.organization_id } : { games_id: s.games_id });

export const entriesKeys = {
	all: ["entries"] as const,
	organizations: (q: OrganizationsQuery) => ["entries", "organizations", q] as const,
	eoi: (s: EntryScope) => ["entries", "eoi", s.games_id, s.organization_id ?? null] as const,
	ebn: (s: EntryScope) => ["entries", "ebn", s.games_id, s.organization_id ?? null] as const,
	matrix: (q: { games_id: string; phase: EntryPhase; page: number; page_size: number }) => ["entries", "matrix", q] as const,
	byEvent: (gamesId: string, sportId: string) => ["entries", "by-event", gamesId, sportId] as const,
	analytics: (q: EntryAnalyticsParams) => ["entries", "analytics", q.games_id, [...(q.organization_ids ?? [])], q.sport_id ?? null] as const,
};

/** `GET /entries/analytics` filters as the dashboard holds them (ids, not the wire's comma list). */
export interface EntryAnalyticsParams {
	games_id: string;
	organization_ids?: readonly string[];
	sport_id?: string;
}

/** `GET /entries/organizations` — the NOCs the caller may act for, with both phases' counts and windows (ONE aggregate). */
/** Sort and dir only when set, so the default list shares its cache with the statistics' and the Strength map's. */
type OrganizationsQuery = { games_id: string; page: number; page_size: number; search?: string; sort?: EntryOrgSort; dir?: "asc" | "desc" };
export const entryOrganizationsOptions = (q: OrganizationsQuery) =>
	queryOptions({
		queryKey: entriesKeys.organizations(q),
		queryFn: ({ signal }) => api.get<EntryOrganizationsResponse>("/entries/organizations", { query: { ...q }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** `GET /entries/eoi` — every sport of the games with the organisation's estimates. */
export const eoiOptions = (s: EntryScope, enabled = true) =>
	queryOptions({
		queryKey: entriesKeys.eoi(s),
		queryFn: ({ signal }) => api.get<EoiView>("/entries/eoi", { query: scopeQuery(s), signal }),
		staleTime: 15_000,
		enabled,
	});

/** `GET /entries/ebn` — the EOI sports → disciplines → events with the organisation's numbers and the warnings. */
export const ebnOptions = (s: EntryScope, enabled = true) =>
	queryOptions({
		queryKey: entriesKeys.ebn(s),
		queryFn: ({ signal }) => api.get<EbnView>("/entries/ebn", { query: scopeQuery(s), signal }),
		staleTime: 15_000,
		enabled,
	});

/** `GET /entries/matrix` (admins) — NOCs × sports for one phase, NOCs paged. */
export const entryMatrixOptions = (q: { games_id: string; phase: EntryPhase; page: number; page_size: number }, enabled = true) =>
	queryOptions({
		queryKey: entriesKeys.matrix(q),
		queryFn: ({ signal }) => api.get<EntryMatrix>("/entries/matrix", { query: { ...q }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		enabled,
	});

/** `GET /entries/by-event` (admins) — one sport's events × NOCs. */
export const entryByEventOptions = (gamesId: string, sportId: string | undefined) =>
	queryOptions({
		queryKey: entriesKeys.byEvent(gamesId, sportId ?? ""),
		queryFn: ({ signal }) => api.get<EntryByEvent>("/entries/by-event", { query: { games_id: gamesId, sport_id: sportId }, signal }),
		staleTime: 30_000,
		enabled: !!sportId,
	});

/**
 * `GET /entries/analytics` (admins) — the statistics dashboard, ONE statement
 * (KPIs, by sport / NOC / event, funnel, timeline). Live on the server (never
 * cached there); short-lived here, and the previous answer stays on screen
 * while a filter change refetches (the charts dim, they never blank). Under
 * `["entries", …]`, so `invalidateEntries` refreshes it with every entry write.
 */
export const entryAnalyticsOptions = (q: EntryAnalyticsParams, enabled = true) =>
	queryOptions({
		queryKey: entriesKeys.analytics(q),
		queryFn: ({ signal }) =>
			api.get<EntryAnalytics>("/entries/analytics", {
				query: {
					games_id: q.games_id,
					organization_ids: q.organization_ids && q.organization_ids.length > 0 ? q.organization_ids.join(",") : undefined,
					sport_id: q.sport_id,
				},
				signal,
			}),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
		enabled,
	});

/** `GET /entries/eoi/remove-impact` — the EBN rows removing these sports would delete. Asked right before a save, never cached. */
export const eoiRemoveImpact = (s: EntryScope, sportIds: readonly string[]) =>
	api.get<EoiRemoveImpact>("/entries/eoi/remove-impact", { query: { ...scopeQuery(s), sport_ids: sportIds.join(",") } });

/** Every entries read — overview, editors, matrix, by-event and the analytics dashboard (all under `["entries"]`). */
export const invalidateEntries = () => queryClient.invalidateQueries({ queryKey: entriesKeys.all });

/**
 * The AI analysis of one NOC (admins). Its key is NOT under `["entries"]` on
 * purpose: an EOI / EBN save must not ask for a new analysis — the next
 * visit does (the panel mounts, reads, and asks for a refresh when stale).
 */
export const entryAnalysisKeys = {
	one: (q: EntryAnalysisQueryInput) => ["entry-analysis", q.games_id, q.organization_id] as const,
};

/** `GET /entries/analysis` — the stored analysis and whether the data moved since (never calls Claude). */
export const entryAnalysisOptions = (q: EntryAnalysisQueryInput, enabled = true) =>
	queryOptions({
		queryKey: entryAnalysisKeys.one(q),
		queryFn: ({ signal }) => api.get<EntryAnalysis>("/entries/analysis", { query: { ...q }, signal }),
		enabled,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});

/** `POST /entries/analysis/refresh` — regenerate when stale (single-flight on the server; 503 when Claude is down). */
export const refreshEntryAnalysis = (body: EntryAnalysisQueryInput) => api.post<EntryAnalysisRefresh>("/entries/analysis/refresh", body);

// ── Writes ──────────────────────────────────────────────────────────────────

/** `PUT /entries/eoi` — the whole list of the organisation's EOI sports. */
export const putEoi = (body: PutEoiBody) => api.put<EoiWriteResult>("/entries/eoi", body);

/** `PUT /entries/ebn` — one sport's events. */
export const putEbn = (body: PutEbnBody) => api.put<EbnWriteResult>("/entries/ebn", body);

/** `POST /entries/:phase/submit` — lock the phase for the NOC. */
export const submitEntryPhase = (phase: EntryPhase, body: EntryPhaseBody) => api.post<EntrySubmitResult>(`/entries/${phase}/submit`, body);

/** `POST /entries/:phase/reopen` — SUPER_ADMIN; `reason` is the audit headline. */
export const reopenEntryPhase = (phase: EntryPhase, body: EntryReopenBody) => api.post<EntrySubmitResult>(`/entries/${phase}/reopen`, body);

// ── Entry windows ───────────────────────────────────────────────────────────

export const entryWindowsKeys = {
	all: ["entry-windows"] as const,
	list: (q: EntryWindowsQueryInput) => ["entry-windows", "list", q] as const,
};

/** `GET /entry-windows` — ADMIN / SUPER_ADMIN, offset paged. */
export const entryWindowsListOptions = (q: EntryWindowsQueryInput) =>
	queryOptions({
		queryKey: entryWindowsKeys.list(q),
		queryFn: ({ signal }) => api.get<Paginated<EntryWindow>>("/entry-windows", { query: { ...q }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** A window change moves every phase's open / closed state too. */
export const invalidateEntryWindows = () =>
	Promise.all([queryClient.invalidateQueries({ queryKey: entryWindowsKeys.all }), queryClient.invalidateQueries({ queryKey: entriesKeys.all })]);

export const createEntryWindow = (body: EntryWindowBody) => api.post<EntryWindow>("/entry-windows", body);
export const updateEntryWindow = (id: string, body: PatchEntryWindowBody) => api.patch<EntryWindow>(`/entry-windows/${id}`, body);
export const deleteEntryWindow = (id: string, reason?: string) => api.delete<null>(`/entry-windows/${id}`, undefined, { query: { reason } });

// ── CSV ─────────────────────────────────────────────────────────────────────

/**
 * `GET /entries/export.csv?games_id&phase` (admins) → a file save. Not JSON,
 * so it bypasses `api` — the Shortlist export's handling: a token refresh
 * once on an expired token, our envelope's code on a failure.
 */
export async function downloadEntriesCsv(gamesId: string, phase: EntryPhase, filename: string): Promise<void> {
	const url = new URL(`${BASE_URL}/entries/export.csv`, window.location.origin);
	url.searchParams.set("games_id", gamesId);
	url.searchParams.set("phase", phase);
	const attempt = async (): Promise<{ blob: Blob } | { error: ApiError }> => {
		const token = getAccessToken();
		let res: Response;
		try {
			res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "omit" });
		} catch (err) {
			if (err instanceof DOMException && err.name === "AbortError") throw err;
			return { error: new ApiError("SERVICE_UNAVAILABLE", 0, "Can't reach the server right now.") };
		}
		if (res.ok) return { blob: await res.blob() };
		try {
			const body = (await res.json()) as { error?: { code?: ApiError["code"]; message?: string; details?: Record<string, unknown> } };
			if (body.error?.code) return { error: new ApiError(body.error.code, res.status, body.error.message ?? "", body.error.details) };
		} catch {
			/* not our envelope */
		}
		return { error: new ApiError("SERVICE_UNAVAILABLE", res.status, "Can't reach the server right now.") };
	};
	if (!getAccessToken()) await api.get("/auth/me");
	let out = await attempt();
	if ("error" in out && isRefreshable(out.error.code)) {
		await api.get("/auth/me");
		out = await attempt();
	}
	if ("error" in out) throw out.error;
	saveBlob(out.blob, filename);
}
